import { ReactNode, useEffect, useState } from 'react';
import { Pagination, Table } from 'react-bootstrap';

export interface TableColumn {
    text: ReactNode;
    dataField: string;
    formatter?: (cell: unknown, row: object, rowIndex: number) => ReactNode;
    headerStyle?: () => React.CSSProperties;
}

interface PaginatedTableProps {
    keyField: string;
    data: object[];
    columns: TableColumn[];
    pageSize?: number;
}

const valueFor = (row: object, field: string): unknown =>
    (row as Record<string, unknown>)[field];

const displayValue = (value: unknown): ReactNode => {
    if (value === null || value === undefined) {
        return '';
    }

    return String(value);
};

const PaginatedTable = ({ keyField, data, columns, pageSize = 20 }: PaginatedTableProps) => {
    const [page, setPage] = useState(1);
    const pageCount = Math.max(1, Math.ceil(data.length / pageSize));
    const firstRowIndex = (page - 1) * pageSize;
    const rows = data.slice(firstRowIndex, firstRowIndex + pageSize);

    useEffect(() => {
        setPage(currentPage => Math.min(currentPage, pageCount));
    }, [pageCount]);

    return (
        <>
            <Table responsive striped bordered hover>
                <thead>
                    <tr>
                        {columns.map((column, columnIndex) => (
                            <th key={`${column.dataField}-${columnIndex}`} style={column.headerStyle?.()}>
                                {column.text}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, rowIndex) => (
                        <tr key={`${String(valueFor(row, keyField))}-${firstRowIndex + rowIndex}`}>
                            {columns.map((column, columnIndex) => {
                                const cell = valueFor(row, column.dataField);

                                return (
                                    <td key={`${column.dataField}-${columnIndex}`}>
                                        {column.formatter
                                            ? column.formatter(cell, row, firstRowIndex + rowIndex)
                                            : displayValue(cell)}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </Table>

            {pageCount > 1 && (
                <Pagination>
                    <Pagination.Prev
                        disabled={page === 1}
                        onClick={() => setPage(currentPage => Math.max(1, currentPage - 1))}
                    />
                    {Array.from({ length: pageCount }, (_, index) => index + 1).map(pageNumber => (
                        <Pagination.Item
                            active={pageNumber === page}
                            key={pageNumber}
                            onClick={() => setPage(pageNumber)}
                        >
                            {pageNumber}
                        </Pagination.Item>
                    ))}
                    <Pagination.Next
                        disabled={page === pageCount}
                        onClick={() => setPage(currentPage => Math.min(pageCount, currentPage + 1))}
                    />
                </Pagination>
            )}
        </>
    );
};

export default PaginatedTable;
